// Library methods
import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";

// Utilities
import { addExam as examAdd } from "../../services/Exam";

import { examCardEnums, eyeTypes } from "../../enums";
import useClinic from "../useClinic";
import { ToastContext } from "../../contexts/ToastContext";
import { useAddEditExamHelpers } from "./useAddEditExamHelpers";

export const useAddExams = () => {
  const { clinicId } = useClinic();
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const { setToast } = useContext(ToastContext);

  const { eyeLeft, eyeRight, eyeBoth } = eyeTypes;

  const { getVfEyeSection, getEyeSection, getSectionName } =
    useAddEditExamHelpers();

  const getVfSections = useCallback(
    ({ selectedExam }) => {
      const { eyeCheckboxes } = selectedExam;

      // array for left and right eye data
      const vfSections = [];

      if (eyeCheckboxes.left) {
        vfSections.push(getVfEyeSection(eyeLeft, selectedExam));
      }
      if (eyeCheckboxes.right) {
        vfSections.push(getVfEyeSection(eyeRight, selectedExam));
      }

      return vfSections;
    },
    [getVfEyeSection, eyeLeft, eyeRight]
  );

  const getNonVfSections = useCallback(
    ({ selectedExam }) => {
      const { eyeCheckboxes, binocular, sectionOrder } = selectedExam;
      let eyeSection = getEyeSection(eyeBoth, selectedExam);

      // array for binocular or left/right eye data
      const sections = [];

      if (binocular) {
        sections.push(eyeSection);
      } else {
        if (eyeCheckboxes.left) {
          sections.push({
            ...eyeSection,
            eye: eyeLeft,
            order: sectionOrder.left,
          });
        }
        if (eyeCheckboxes.right) {
          sections.push({
            ...eyeSection,
            eye: eyeRight,
            order: sectionOrder.right,
          });
        }
      }

      return sections;
    },
    [getEyeSection, eyeBoth, eyeLeft, eyeRight]
  );

  const addExamData = useCallback(
    async ({ scheduledDate, sectionTypeOrder, examSections, patientId }) => {
      const examData = {
        sectionTypeOrder: sectionTypeOrder.join(", "),
        scheduledDate,
        ...examSections,
      };

      // add exam to exams api
      let newExam = null;

      try {
        const token = await getAccessTokenSilently();
        newExam = await examAdd(token, clinicId, patientId, examData);
      } catch (error) {
        setToast(() => ({
          success: false,
          message: t("exams_add_error"),
        }));
      } finally {
        return newExam;
      }
    },
    [getAccessTokenSilently, clinicId, setToast, t]
  );

  const getFormattedExamSections = useCallback(
    ({ selectedExamCards }) => {
      let examSections = {};

      selectedExamCards.forEach((selectedExam) => {
        const { examFieldType } = selectedExam;
        const sectionName = getSectionName(selectedExam);

        switch (examFieldType) {
          // visual field exams (except esterman)
          case examCardEnums.VISUAL_FIELD_SCREENING.algorithmPath:
          case examCardEnums.VISUAL_FIELD_THRESHOLDING.algorithmPath:
          case examCardEnums.VISUAL_FIELD_FULL_120.algorithmPath:
            const vfSections = getVfSections({
              selectedExam,
            });
            examSections[sectionName] = vfSections;
            break;

          // esterman exams
          case examCardEnums.VISUAL_FIELD_ESTERMAN.algorithmPath:
            const estermanSection = getVfEyeSection(eyeBoth, selectedExam);
            examSections[sectionName] = [estermanSection];
            break;

          // color vision, contrast sensitivity, visual acuity, synoptophore, RAPD
          default:
            const sections = getNonVfSections({
              selectedExam,
            });
            examSections[sectionName] = sections;
        }
      });

      return examSections;
    },
    [eyeBoth, getSectionName, getVfSections, getVfEyeSection, getNonVfSections]
  );

  // add an exam
  const addExam = useCallback(
    async ({
      selectedExamCards,
      scheduledDate,
      sectionTypeOrder,
      patientId,
      onAddExamCallback,
    }) => {
      const examSections = getFormattedExamSections({ selectedExamCards });
      const newExam = await addExamData({
        scheduledDate: scheduledDate.toUTC().toISO(),
        sectionTypeOrder,
        examSections,
        patientId,
      });
      await onAddExamCallback(newExam);
    },
    [getFormattedExamSections, addExamData]
  );

  return {
    addExam,
    getFormattedExamSections,
  };
};
